/**
 * React Query hooks for creative management
 * Provides type-safe data fetching and mutations for creatives
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';

import { creativesKeys, projectsKeys } from '@/lib/queryClient';
import { api } from '@/services/api';
import type {
  Creative,
  CreateCreativeRequest,
  UpdateCreativeRequest,
  UploadUrlResponse,
  AnalysisDecision,
  PerformanceMetrics,
} from '@/types';
import type { PerformanceContextResponse } from '@/types/metrics';

// ============================================================================
// QUERY HOOKS
// ============================================================================

/**
 * Fetch all creatives for a project
 */
export function useCreatives(
  projectId?: string
): UseQueryResult<Creative[], Error> {
  return useQuery({
    queryKey: creativesKeys.list({ projectId }),
    queryFn: async () => {
      if (!projectId) {
        return [];
      }
      const creatives = await api.get<Creative[]>(
        `/projects/${projectId}/creatives`
      );
      return creatives;
    },
    enabled: !!projectId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Fetch a single creative by ID
 */
export function useCreative(
  creativeId: string
): UseQueryResult<Creative, Error> {
  return useQuery({
    queryKey: creativesKeys.detail(creativeId),
    queryFn: async () => {
      const creative = await api.get<Creative>(`/creatives/${creativeId}`);
      return creative;
    },
    enabled: !!creativeId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Fetch creatives filtered by decision
 */
export function useCreativesByDecision(
  projectId?: string,
  decision?: AnalysisDecision
): UseQueryResult<Creative[], Error> {
  const creativesQuery = useCreatives(projectId);

  return {
    ...creativesQuery,
    data: creativesQuery.data?.filter(
      (creative) => creative.latestDecision === decision
    ),
  } as UseQueryResult<Creative[], Error>;
}

// ============================================================================
// MUTATION HOOKS
// ============================================================================

/**
 * Get upload URL for a creative
 */
export function useUploadUrl(): UseMutationResult<
  UploadUrlResponse,
  Error,
  {
    projectId: string;
    fileName: string;
    fileSize: number;
    contentType: string;
  }
> {
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post<UploadUrlResponse>(
        '/creatives/upload-url',
        data
      );
      return response;
    },
    onError: (error) => {
      console.error('Failed to get upload URL:', error);
    },
  });
}

/**
 * Create a new creative
 */
export function useCreateCreative(): UseMutationResult<
  Creative,
  Error,
  CreateCreativeRequest
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateCreativeRequest) => {
      const creative = await api.post<Creative>(
        `/projects/${data.projectId}/creatives`,
        data
      );
      return creative;
    },
    onSuccess: (newCreative, variables) => {
      // Invalidate creatives list to refetch
      queryClient.invalidateQueries({
        queryKey: creativesKeys.list({ projectId: variables.projectId }),
      });

      // Optimistically add the new creative to the cache
      queryClient.setQueryData<Creative[]>(
        creativesKeys.list({ projectId: variables.projectId }),
        (old) => {
          if (!old) return [newCreative];
          return [...old, newCreative];
        }
      );

      // Set the new creative detail in cache
      queryClient.setQueryData(
        creativesKeys.detail(newCreative.creativeId),
        newCreative
      );

      // Update project creative count
      queryClient.invalidateQueries({
        queryKey: projectsKeys.detail(variables.projectId),
      });
      queryClient.invalidateQueries({
        queryKey: projectsKeys.stats(variables.projectId),
      });
    },
    onError: (error) => {
      console.error('Failed to create creative:', error);
    },
  });
}

/**
 * Update an existing creative
 */
export function useUpdateCreative(
  creativeId: string
): UseMutationResult<Creative, Error, UpdateCreativeRequest> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateCreativeRequest) => {
      const creative = await api.put<Creative>(
        `/creatives/${creativeId}`,
        data
      );
      return creative;
    },
    onSuccess: (updatedCreative) => {
      // Update the creative in the detail cache
      queryClient.setQueryData(
        creativesKeys.detail(creativeId),
        updatedCreative
      );

      // Update the creative in the list cache
      queryClient.setQueryData<Creative[]>(
        creativesKeys.list({ projectId: updatedCreative.projectId }),
        (old) => {
          if (!old) return [updatedCreative];
          return old.map((creative) =>
            creative.creativeId === creativeId ? updatedCreative : creative
          );
        }
      );

      // Invalidate to ensure consistency
      queryClient.invalidateQueries({
        queryKey: creativesKeys.lists(),
      });
    },
    onError: (error) => {
      console.error('Failed to update creative:', error);
    },
  });
}

/**
 * Delete a creative
 */
export function useDeleteCreative(
  creativeId: string
): UseMutationResult<void, Error, void> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await api.delete(`/creatives/${creativeId}`);
    },
    // DELETE operations should not retry - they're destructive and not idempotent
    retry: false,
    onMutate: async () => {
      // Get the creative to know which project list to update
      const creative = queryClient.getQueryData<Creative>(
        creativesKeys.detail(creativeId)
      );
      return { projectId: creative?.projectId };
    },
    onSuccess: (_, __, context) => {
      if (context?.projectId) {
        // Remove from list cache
        queryClient.setQueryData<Creative[]>(
          creativesKeys.list({ projectId: context.projectId }),
          (old) => {
            if (!old) return [];
            return old.filter((creative) => creative.creativeId !== creativeId);
          }
        );

        // Update project creative count
        queryClient.invalidateQueries({
          queryKey: projectsKeys.detail(context.projectId),
        });
        queryClient.invalidateQueries({
          queryKey: projectsKeys.stats(context.projectId),
        });
      }

      // Remove detail cache
      queryClient.removeQueries({
        queryKey: creativesKeys.detail(creativeId),
      });

      // Invalidate to ensure consistency
      queryClient.invalidateQueries({ queryKey: creativesKeys.lists() });
    },
    onError: (error) => {
      console.error('Failed to delete creative:', error);
    },
  });
}

// ============================================================================
// HELPER HOOKS
// ============================================================================

/**
 * Get recent creatives for a project (sorted by createdAt)
 */
export function useRecentCreatives(
  projectId?: string,
  limit: number = 10
): UseQueryResult<Creative[], Error> {
  const creativesQuery = useCreatives(projectId);

  return {
    ...creativesQuery,
    data: creativesQuery.data
      ?.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )
      .slice(0, limit),
  } as UseQueryResult<Creative[], Error>;
}

/**
 * Get creative stats for a project
 */
export function useCreativeStats(projectId?: string): {
  total: number;
  analyzed: number;
  notAnalyzed: number;
  scale: number;
  fix: number;
  kill: number;
  isLoading: boolean;
} {
  const { data: creatives, isLoading } = useCreatives(projectId);

  if (isLoading || !creatives) {
    return {
      total: 0,
      analyzed: 0,
      notAnalyzed: 0,
      scale: 0,
      fix: 0,
      kill: 0,
      isLoading,
    };
  }

  const total = creatives.length;
  const analyzed = creatives.filter((c) => c.latestDecision).length;
  const notAnalyzed = total - analyzed;
  const scale = creatives.filter((c) => c.latestDecision === 'Scale').length;
  const fix = creatives.filter((c) => c.latestDecision === 'Fix').length;
  const kill = creatives.filter((c) => c.latestDecision === 'Kill').length;

  return {
    total,
    analyzed,
    notAnalyzed,
    scale,
    fix,
    kill,
    isLoading: false,
  };
}

// ============================================================================
// PERFORMANCE METRICS HOOKS
// ============================================================================

/**
 * Update creative's performance metrics
 * Uses existing PUT /creatives/{creativeId} endpoint
 */
export function useUpdateCreativeMetrics(
  creativeId: string
): UseMutationResult<Creative, Error, PerformanceMetrics> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (performanceMetrics: PerformanceMetrics) => {
      const creative = await api.put<Creative>(
        `/creatives/${creativeId}`,
        { performanceMetrics }
      );
      return creative;
    },
    onSuccess: (updatedCreative) => {
      // Update the creative in the detail cache
      queryClient.setQueryData(
        creativesKeys.detail(creativeId),
        updatedCreative
      );

      // Update the creative in the list cache
      queryClient.setQueryData<Creative[]>(
        creativesKeys.list({ projectId: updatedCreative.projectId }),
        (old) => {
          if (!old) return [updatedCreative];
          return old.map((creative) =>
            creative.creativeId === creativeId ? updatedCreative : creative
          );
        }
      );

      // Invalidate performance context since metrics have changed
      queryClient.invalidateQueries({
        queryKey: creativesKeys.performanceContext(creativeId),
      });

      // Invalidate to ensure consistency
      queryClient.invalidateQueries({
        queryKey: creativesKeys.lists(),
      });
    },
    onError: (error) => {
      console.error('Failed to update performance metrics:', error);
    },
  });
}

/**
 * Fetch performance context with percentile comparison
 * Supports scope params for metric scope (all/bucket/goal), project scope (project/team),
 * date range (startDate/endDate), and peer media type (peerMediaType).
 */
export function usePerformanceContext(
  creativeId?: string,
  metricScope?: "all" | "bucket" | "goal",
  projectScope?: "project" | "team",
  startDate?: string,
  endDate?: string,
  peerMediaType?: "video" | "image",
): UseQueryResult<PerformanceContextResponse, Error> {
  return useQuery({
    queryKey: creativesKeys.performanceContext(creativeId || '', {
      metricScope,
      projectScope,
      startDate,
      endDate,
      peerMediaType,
    }),
    queryFn: async () => {
      if (!creativeId) {
        return {
          hasMetrics: false,
          message: 'Creative ID is required',
          percentiles: null,
          sampleSize: 0,
          scope: {
            metricScope: 'all' as const,
            metricScopeLabel: 'All',
            projectScope: 'project' as const,
            availableMetricScopes: ['all' as const],
            optimizationGoal: null,
            objectiveBucket: null,
          },
          warnings: [],
        };
      }
      const params = new URLSearchParams();
      if (metricScope) params.append('metricScope', metricScope);
      if (projectScope) params.append('projectScope', projectScope);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (peerMediaType) params.append('mediaType', peerMediaType);
      const queryString = params.toString();
      const url = `/creatives/${creativeId}/performance-context${queryString ? `?${queryString}` : ''}`;
      const data = await api.get<PerformanceContextResponse>(url);
      return data;
    },
    enabled: !!creativeId,
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: keepPreviousData,
  });
}

// ============================================================================
// COPY CREATIVE HOOK
// ============================================================================

/**
 * Copy a creative to another project
 * POST /creatives/{creativeId}/copy
 */
export function useCopyCreative(
  creativeId: string
): UseMutationResult<Creative, Error, { targetProjectId: string }> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ targetProjectId }) => {
      const creative = await api.post<Creative>(
        `/creatives/${creativeId}/copy`,
        { targetProjectId }
      );
      return creative;
    },
    onSuccess: (newCreative) => {
      // Invalidate the target project's creatives list
      queryClient.invalidateQueries({
        queryKey: creativesKeys.list({ projectId: newCreative.projectId }),
      });

      // Update target project stats
      queryClient.invalidateQueries({
        queryKey: projectsKeys.detail(newCreative.projectId),
      });
      queryClient.invalidateQueries({
        queryKey: projectsKeys.stats(newCreative.projectId),
      });

      // Set the new creative detail in cache
      queryClient.setQueryData(
        creativesKeys.detail(newCreative.creativeId),
        newCreative
      );
    },
    onError: (error) => {
      console.error('Failed to copy creative:', error);
    },
  });
}
